import { useEffect, useRef } from "react";
import { ANALYTICS_TRACK_DELAY_MS } from "@acto/taskpane/core/analytics";

/**
 * Fires `fire()` once, `ANALYTICS_TRACK_DELAY_MS` after mount (or after `resetKey` changes),
 * to collapse rapid filter/KPI/drill toggles into a single representative "viewed" event.
 *
 * The timer is armed only by `resetKey` (mount-only when omitted) — NOT by `fire`'s identity —
 * so a `fire` callback that changes identity later (e.g. the analytics provider's `track`
 * refreshing after an auth/tenant-data update) never re-arms or double-fires an event that
 * already fired. `fire` is still read through a ref so the eventual call uses the latest
 * closure (current KPI/filters/etc.), not the one captured at mount.
 */
export function useDelayedViewTrack(fire: () => void, resetKey?: string): void {
  const fireRef = useRef(fire);
  fireRef.current = fire;

  useEffect(() => {
    const timer = setTimeout(() => fireRef.current(), ANALYTICS_TRACK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [resetKey]);
}
